import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import jwt_decode, { JwtPayload } from 'jwt-decode';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { API } from '../../constants/api';
import { MESSAGES } from '../../constants/messages';
import { ROUTES } from '../../constants/routes';
import { User } from '../../models/auth/user.model';
import { FeedbackService } from '../feedback/feedback.service';
import { IDBService } from '../idb/idb.service';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  $currentUserSubject: BehaviorSubject<User>;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private storageService: StorageService,
    private feedbackService: FeedbackService,
    private ibdService: IDBService,
  ) {
    const userObj = this.getCurrentUser();
    this.$currentUserSubject = new BehaviorSubject(userObj);
  }

  public auth(username: string, password: string): Observable<any> {
    return this.httpClient
      .post(API.LOGIN, { username, password })
      .pipe(catchError((error) => throwError(error)));
  }

  public getUserByName(username: string): Observable<any> {
    return this.httpClient
      .get(`${API.USERS}/${username}`)
      .pipe(catchError((error) => throwError(error)));
  }

  public async loginFromSubWeb(token: string) {
    try {
      const decodedToken: JwtPayload = jwt_decode(token);
      const username = decodedToken.sub;

      if (this.getCurrentUser()?.username === username && this.getCurrentUser()?.token === token) {
        return;
      }

      const data = await this.getUserByName(username).toPromise();
      const subwebUser: User = data;
      subwebUser.token = token;
      subwebUser.active = true;
      this.storageService.set(CONSTANTS.STORAGE.USER, subwebUser);
      this.$currentUserSubject.next(subwebUser);
      this.router.navigate([ROUTES.DASHBOARD.PATH, ROUTES.SHEET.PATH]);
    } catch (err) {
      this.feedbackService.error(MESSAGES.ERROR.LOGIN);
    }
  }

  public login(user: User): void {
    const userObject = user;
    this.storageService.set(CONSTANTS.STORAGE.USER, userObject);
    this.$currentUserSubject.next(userObject);
  }

  public getCurrentUser(): User {
    return this.storageService.get(CONSTANTS.STORAGE.USER);
  }

  public isLoggedIn(): boolean {
    return !!this.storageService.get(CONSTANTS.STORAGE.USER);
  }

  public logout(): void {
    this.removeSensitiveData();
    this.router.navigate([ROUTES.LOGIN]);
  }

  public removeSensitiveData(): void {
    this.ibdService.delete(CONSTANTS.STORAGE.SIMULATION);
    this.storageService.delete(CONSTANTS.STORAGE.USER);
    this.storageService.delete(CONSTANTS.STORAGE.REFRESH_TOKEN);
    this.storageService.delete(CONSTANTS.STORAGE.PERMISSIONS);
  }

  public getAccessToken(): string {
    return this.storageService.get(CONSTANTS.STORAGE.USER);
  }

  public getRefreshToken(): string {
    return this.storageService.get(CONSTANTS.STORAGE.REFRESH_TOKEN);
  }

  public getToken(): string | null {
    return this.currentUser?.token || null;
  }

  get currentUser(): User {
    return this.$currentUserSubject.getValue();
  }
}
